<template>
  <div class="text-body text-black-2">
    <template v-if="inherited">
      Inherited from <RouterLink :to="inherited.link">{{ inherited.name }}</RouterLink>
    </template>
    <div v-else-if="schedule.startDate || schedule.endDate" class="flex flex-col">
      <div class="flex items-center gap-1">
        <div>
          {{ startDate }}
          <span v-if="(!isPushNotification || frequency || isEventTriggered) && !hideEndDate">
            - {{ endDate || "∞" }}
          </span>
          <span v-if="isPushNotification && (frequency || isLocalTime) && !isEventTriggered">
            <span> (</span>
            <span v-if="frequency">every {{ frequency?.replace(".", " ") }}</span>
            <span v-if="frequency && isLocalTime">, </span>
            <span v-if="isLocalTime">local time</span>
            <span>)</span>
          </span>
        </div>
        <RfTooltip v-if="timezone" class="mr-0 w-fit">
          <template #activator> <RfHelpIcon class="h-6! w-6!" /> </template>
          <template #tooltip>
            <div>Time zone: {{ props.timezone }}</div>
          </template>
        </RfTooltip>
      </div>

      <div v-if="schedule.daypart?.length" class="flex flex-col">
        Daypart:
        <div v-for="(item, index) in schedule.daypart" :key="index">
          {{ WeekDay[item[0]] }}, {{ StringUtils.hhmmDisplay(item[1]) }} -
          {{ StringUtils.hhmmDisplay(item[2]) }}
        </div>
      </div>

      <div v-if="isPushNotification && quietTimeStart && quietTimeEnd">
        Quiet time: {{ quietTimeStart }} - {{ quietTimeEnd }}
      </div>
    </div>
    <span v-else>-</span>
  </div>
</template>

<script setup>
import { RouterLink } from "vue-router";
import { computed } from "vue";
import { dateDisplay } from "@/utils/DateDisplayUtils";
import StringUtils from "@/utils/StringUtils";
import RfTooltip from "@/components/tooltip/RfTooltip.vue";
import RfHelpIcon from "@/components/icons/RfHelpIcon.vue";
import TimezonesConstants from "@/utils/constants/TimezonesConstants";

const WeekDay = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const props = defineProps({
  inherited: { type: Object, default: null },
  schedule: { type: Object, default: () => ({}) },
  isPushNotification: { type: Boolean, default: false },
  actions: { type: Object, default: () => ({}) },
  timezone: { type: String, default: null },
  hideEndDate: { type: Boolean, default: false },
  // RE-1413: schedule values are naive app-timezone wall clocks, not instants — skip the
  // timezone conversion here or the displayed date shifts. See Experiment#as_json.
  naiveSchedule: { type: Boolean, default: false },
});

const isEventTriggered = computed(
  () => props.actions?.rf_pinpoint_campaign_event_triggered === "true",
);
const frequency = computed(() => props.actions?.rf_push_frequency);
const quietTimeStart = computed(() => props.actions?.rf_pinpoint_campaign_quiet_time_start);
const quietTimeEnd = computed(() => props.actions?.rf_pinpoint_campaign_quiet_time_end);
const isLocalTime = computed(() => props.actions?.rf_push_use_local_time === "true");
const displayTimezone = computed(() =>
  props.naiveSchedule ? null : TimezonesConstants[props.timezone],
);
const startDate = computed(() =>
  dateDisplay(props.schedule.startDate, {
    alter: true,
    time: true,
    utc: true,
    timezone: displayTimezone.value,
  }),
);
const endDate = computed(() =>
  dateDisplay(props.schedule.endDate, {
    alter: true,
    time: true,
    utc: true,
    timezone: displayTimezone.value,
  }),
);
</script>
